<template>
  <div v-if="visible" class="archive-overlay" @click.self="$emit('close')">
    <div class="archive-modal">
      <!-- Header -->
      <div class="archive-header">
        <div class="header-left">
          <h2 class="header-title">
            <i class="fas fa-archive"></i>
            Archiv
          </h2>
          <div class="order-count">
            {{ displayOrders.length }} Bestellungen
          </div>
        </div>
        
        <div class="header-right">
          <button class="close-btn" @click="$emit('close')">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>
      
      <!-- Toolbar -->
      <div class="archive-toolbar">
        <div class="search-box">
          <i class="fas fa-search search-icon"></i>
          <input
            v-model="searchQuery"
            type="text"
            class="search-input"
            placeholder="Bestellungen suchen..."
            @input="handleSearch"
          >
        </div>
        
        <div class="action-buttons">
          <button @click="clearArchive" class="action-btn clear">
            <i class="fas fa-trash"></i>
            <span class="btn-text">{{ $t('clear') || 'Löschen' }}</span>
          </button>
        </div>
      </div>
      
      <!-- Content Area -->
      <div class="archive-content">
        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
          <i class="fas fa-spinner fa-spin"></i>
          <p>Lädt...</p>
        </div>

        <!-- Empty State -->
        <div v-else-if="displayOrders.length === 0" class="empty-state">
          <i class="fas fa-inbox"></i>
          <p>Keine Bestellungen im Archiv</p>
        </div>
        
        <!-- Orders List -->
        <div v-else class="orders-list">
          <div 
            v-for="order in displayOrders" 
            :key="order.id"
            class="order-item"
            @click="viewOrderDetails(order)"
          >
            <!-- Order Header -->
            <div class="order-header">
              <div class="order-info">
                <span class="order-type">
                  {{ getOrderType(order) }}
                </span>
                <span class="order-number">#{{ order.orderNumber || order.id?.substring(0, 6) }}</span>
              </div>
              <div class="order-time">
                <span class="time">{{ formatTime(order.completedAt || order.archivedAt) }}</span>
                <span class="date">{{ formatDate(order.completedAt || order.archivedAt) }}</span>
              </div>
            </div>
            
            <!-- Order Items -->
            <div class="order-items">
              <div v-for="(item, idx) in getOrderItems(order).slice(0, 2)" :key="idx" class="item-row">
                <span class="item-qty">{{ item.quantity || 1 }}x</span>
                <span class="item-name">{{ getItemName(item) }}</span>
              </div>
              <div v-if="getOrderItems(order).length > 2" class="more-items">
                +{{ getOrderItems(order).length - 2 }} Weitere
              </div>
            </div>
            
            <!-- Order Footer -->
            <div class="order-footer">
              <span class="status-badge completed">
                <i class="fas fa-check-circle"></i>
                Abgeschlossen
              </span>
              <div class="order-meta">
                <span v-if="order.waiterName" class="meta-item">
                  <i class="fas fa-user"></i> {{ order.waiterName }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Footer -->
      <div class="archive-footer">
        <div class="footer-info">
          <span v-if="archiveMode === 'local'" class="storage-info">
            <i class="fas fa-database"></i>
            {{ storageInfo.sizeInKB }} KB / {{ storageInfo.percentUsed }}%
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import localArchiveService from '../services/localArchiveService'

export default {
  name: 'NewArchiveModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      searchQuery: '',
      loading: false,
      cloudOrders: {},
      storageInfo: {
        orderCount: 0,
        sizeInKB: '0',
        percentUsed: '0'
      },
      searchTimeout: null
    }
  },
  computed: {
    ...mapState(['localArchiveOrders', 'archiveMode', 'selectedStation', 'language']),
    
    displayOrders() {
      let orders = [];
      
      if (this.archiveMode === 'local') {
        orders = this.searchQuery 
          ? localArchiveService.searchOrders(this.searchQuery)
          : this.localArchiveOrders;
      } else {
        orders = Object.entries(this.cloudOrders).map(([id, order]) => ({
          ...order,
          id
        }));
      }
      
      // Sort by date (newest first)
      return orders.sort((a, b) => {
        const dateA = new Date(a.completedAt || a.archivedAt || a.timestamp);
        const dateB = new Date(b.completedAt || b.archivedAt || b.timestamp);
        return dateB - dateA;
      });
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.loadArchiveData();
        this.updateStorageInfo();
      }
    }
  },
  methods: {
    ...mapActions([
      'loadLocalArchive',
      'clearLocalArchive',
      'exportLocalArchive',
      'importLocalArchive'
    ]),
    
    setMode(mode) {
      this.$store.commit('SET_ARCHIVE_MODE', mode);
      localStorage.setItem('archiveMode', mode);
      this.loadArchiveData();
    },
    
    async loadArchiveData() {
      if (this.archiveMode === 'local') {
        await this.loadLocalArchive();
      } else {
        await this.loadCloudArchive();
      }
    },
    
    async loadCloudArchive() {
      if (!this.$store.state.firebase || !this.$store.state.selectedRestaurantId) return;
      
      this.loading = true;
      try {
        const { ref, get } = await import('firebase/database');
        const db = this.$store.state.firebase.database();
        const ordersRef = ref(db, `restaurants/${this.$store.state.selectedRestaurantId}/completedOrders`);
        const snapshot = await get(ordersRef);
        
        this.cloudOrders = snapshot.exists() ? snapshot.val() : {};
      } catch (error) {
        console.error('Error loading cloud archive:', error);
        this.cloudOrders = {};
      } finally {
        this.loading = false;
      }
    },
    
    refreshCloudArchive() {
      this.loadCloudArchive();
    },
    
    handleSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        // Search is handled by computed property
      }, 300);
    },
    
    getOrderItems(order) {
      if (order.items && Array.isArray(order.items)) {
        return order.items;
      }
      
      if (this.selectedStation?.destinationCode && order[this.selectedStation.destinationCode]) {
        return order[this.selectedStation.destinationCode].items || [];
      }
      
      const sections = ['K', 'D', 'S'];
      for (const section of sections) {
        if (order[section] && order[section].items) {
          return order[section].items;
        }
      }
      
      return [];
    },
    
    formatDate(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    },
    
    formatTime(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp);
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    },
    
    viewOrderDetails(order) {
      this.$emit('viewOrder', order);
    },
    
    async clearArchive() {
      if (confirm('Sind Sie sicher, dass Sie das Archiv löschen möchten?')) {
        await this.clearLocalArchive();
        this.updateStorageInfo();
      }
    },
    
    exportArchive() {
      this.exportLocalArchive();
    },
    
    async handleImport(event) {
      const file = event.target.files[0];
      if (!file) return;
      
      try {
        const count = await this.importLocalArchive(file);
        alert(`${this.$t('imported') || 'تم استيراد'} ${count} ${this.$t('orders') || 'طلبات'}`);
        this.updateStorageInfo();
      } catch (error) {
        alert(`${this.$t('importFailed') || 'فشل الاستيراد'}: ${error.message}`);
      }
      
      this.$refs.importInput.value = '';
    },
    
    updateStorageInfo() {
      this.storageInfo = localArchiveService.getStorageInfo();
    },
    
    getItemName(item) {
      // Display name based on current language setting
      if (this.language === 'ar') {
        // For Arabic, prioritize Arabic names
        return item.name_ar || item.itemName_ar || item.name || item.itemName || 'عنصر';
      } else {
        // For German/English, prioritize regular names
        return item.name || item.itemName || item.name_ar || item.itemName_ar || 'Item';
      }
    },

    getOrderType(order) {
      // For MealWave orders with type field
      if (order.type === 'delivery') {
        return 'Lieferung';
      }
      if (order.type === 'pickup') {
        return 'Abholung';
      }
      // Fallback for table orders
      if (!order.tableNumber || order.tableNumber.toString().toLowerCase() === 'takeaway') {
        return 'Zum Mitnehmen';
      }
      return `Tisch ${order.tableNumber}`;
    }
  }
}
</script>

<style scoped>
/* Overlay */
.archive-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: none;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Modal Container */
.archive-modal {
  background: var(--ods-surface);
  border-radius: var(--ods-radius-lg);
  width: 90%;
  max-width: 1400px;
  height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from { 
    transform: translateY(30px);
    opacity: 0;
  }
  to { 
    transform: translateY(0);
    opacity: 1;
  }
}

/* Header */
.archive-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid var(--ods-line);
  background: linear-gradient(135deg, #2c3444 0%, var(--ods-surface) 100%);
  border-radius: var(--ods-radius-lg) var(--ods-radius-lg) 0 0;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-title {
  color: var(--ods-ink);
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: inherit;
}

.header-title i {
  color: var(--ods-accent-ink);
}

.order-count {
  background: rgba(96, 165, 250, 0.2);
  color: var(--ods-accent-ink);
  padding: 6px 12px;
  border-radius: var(--ods-radius-lg);
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 20px;
}

.mode-toggle {
  display: flex;
  background: var(--ods-surface-2);
  border-radius: var(--ods-radius);
  padding: 4px;
}

.mode-btn {
  padding: 8px 16px;
  border: none;
  background: transparent;
  color: var(--ods-ink-2);
  border-radius: var(--ods-radius);
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  font-family: inherit;
}

.mode-btn.active {
  background: var(--ods-accent);
  color: var(--ods-ink);
}

.mode-btn:hover:not(.active) {
  background: var(--ods-surface-3);
}

.close-btn {
  background: var(--ods-surface-3);
  border: none;
  width: 40px;
  height: 40px;
  border-radius: var(--ods-radius);
  color: var(--ods-ink-2);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.close-btn:hover {
  background: var(--ods-surface-3);
  color: var(--ods-ink);
}

/* Toolbar */
.archive-toolbar {
  padding: 16px 24px;
  display: flex;
  gap: 16px;
  border-bottom: 1px solid var(--ods-line);
  background: var(--ods-surface);
}

.search-box {
  flex: 1;
  position: relative;
}

.search-icon {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--ods-ink-3);
}

.search-input {
  width: 100%;
  padding: 10px 16px 10px 44px;
  background: var(--ods-surface-2);
  border: 1px solid var(--ods-line);
  border-radius: var(--ods-radius);
  color: var(--ods-ink);
  font-size: 14px;
  font-family: inherit;
  transition: all 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: var(--ods-accent);
  background: var(--ods-surface-2);
}

.search-input::placeholder {
  color: var(--ods-ink-3);
}

.action-buttons {
  display: flex;
  gap: 10px;
}

.action-btn {
  padding: 10px 16px;
  border: none;
  border-radius: var(--ods-radius);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
  font-family: inherit;
}

.action-btn.export {
  background: #10b981;
  color: var(--ods-ink);
}

.action-btn.export:hover {
  background: #059669;
}

.action-btn.import {
  background: var(--ods-accent);
  color: var(--ods-ink);
}

.action-btn.import:hover {
  background: var(--ods-accent);
}

.action-btn.clear {
  background: #ef4444;
  color: var(--ods-ink);
}

.action-btn.clear:hover {
  background: #dc2626;
}

.action-btn.refresh {
  background: var(--ods-accent);
  color: var(--ods-ink);
}

.action-btn.refresh:hover {
  background: var(--ods-accent);
}

.btn-text {
  font-family: inherit;
}

.rotating {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.hidden {
  display: none;
}

/* Content Area */
.archive-content {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  background: var(--ods-surface);
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--ods-ink-3);
  gap: 16px;
}

.loading-state i {
  font-size: 48px;
  color: var(--ods-accent-ink);
}

.loading-state p {
  font-size: 16px;
  font-family: inherit;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--ods-ink-3);
  gap: 16px;
}

.empty-state i {
  font-size: 64px;
  opacity: 0.5;
}

.empty-state p {
  font-size: 18px;
  font-family: inherit;
}

/* Orders List */
.orders-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
  animation: fadeInUp 0.3s ease;
}

/* Fixed grid layouts for specific screen sizes */
@media (min-width: 1920px) {
  .orders-list {
    grid-template-columns: repeat(5, 1fr);
  }
}

@media (min-width: 2560px) {
  .orders-list {
    grid-template-columns: repeat(7, 1fr);
  }
}

@media (min-width: 1366px) and (max-width: 1919px) {
  .orders-list {
    grid-template-columns: repeat(4, 1fr);
  }
}

@media (min-width: 1024px) and (max-width: 1365px) {
  .orders-list {
    grid-template-columns: repeat(3, 1fr);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Order Item */
.order-item {
  background: var(--ods-surface);
  border: 1px solid var(--ods-line);
  border-radius: var(--ods-radius);
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.order-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
  border-color: var(--ods-accent);
}

.order-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.order-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.order-type {
  color: var(--ods-ink);
  font-size: 16px;
  font-weight: 600;
  font-family: inherit;
}

.order-number {
  color: var(--ods-ink-3);
  font-size: 13px;
  font-family: monospace;
}

.order-time {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}

.time {
  color: var(--ods-ink-2);
  font-size: 13px;
}

.date {
  color: var(--ods-ink-3);
  font-size: 11px;
}

.order-items {
  padding: 12px 0;
  border-top: 1px solid var(--ods-line);
  border-bottom: 1px solid var(--ods-line);
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-height: 60px;
}

.item-row {
  display: flex;
  gap: 8px;
  color: var(--ods-ink);
  font-size: 14px;
  font-family: inherit;
}

.item-qty {
  color: var(--ods-accent-ink);
  font-weight: 600;
  min-width: 30px;
}

.item-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.more-items {
  color: var(--ods-ink-3);
  font-size: 13px;
  font-style: italic;
  font-family: inherit;
}

.order-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.status-badge {
  padding: 4px 10px;
  border-radius: var(--ods-radius-lg);
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: inherit;
}

.status-badge.completed {
  background: rgba(16, 185, 129, 0.2);
  color: #10b981;
}

.order-meta {
  display: flex;
  gap: 12px;
}

.meta-item {
  color: var(--ods-ink-3);
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.local-indicator {
  color: var(--ods-accent-ink);
}

/* Footer */
.archive-footer {
  padding: 16px 24px;
  border-top: 1px solid var(--ods-line);
  background: var(--ods-surface);
  border-radius: 0 0 var(--ods-radius-lg) var(--ods-radius-lg);
}

.footer-info {
  display: flex;
  justify-content: flex-end;
}

.storage-info {
  color: var(--ods-ink-2);
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: inherit;
}

.storage-info i {
  color: var(--ods-accent-ink);
}

/* Scrollbar */
.archive-content::-webkit-scrollbar {
  width: 8px;
}

.archive-content::-webkit-scrollbar-track {
  background: var(--ods-surface-2);
  border-radius: var(--ods-radius);
}

.archive-content::-webkit-scrollbar-thumb {
  background: var(--ods-surface-3);
  border-radius: var(--ods-radius);
}

.archive-content::-webkit-scrollbar-thumb:hover {
  background: var(--ods-surface-3);
}

/* Responsive */
@media (max-width: 768px) {
  .archive-modal {
    width: 95%;
    height: 90vh;
  }
  
  .orders-list {
    grid-template-columns: 1fr;
  }
  
  .archive-toolbar {
    flex-direction: column;
  }
  
  .action-buttons {
    width: 100%;
    justify-content: space-between;
  }
}
</style>